import React, { useMemo, useState } from 'react'
import { Flex, H1WithBack, PageWrapper, ColorVariant } from '../../../styling/GlobalStyles'
import { useDispatch, useSelector } from 'react-redux'
import { NavBar } from '../../../components/NavBar/NavBar'
import { AppDispatch, RootState } from 'store'
import { formatDateForDisplay, getParams } from 'utils/helpers'
import useQueryPagination from 'hooks/useQueryPagination'
import {
  activatePolicy,
  clearPolicies,
  deletePolicy,
  getPolicies,
} from 'features/attendance/attendanceSlice'
import { NoData } from 'components/NoData'
import { useNavigate } from 'react-router-dom'
import { setConfirmModal } from 'features/global/globalSlice'
import { ModalTypes } from 'types/global.interface'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import { BodyText, OtherText, TitleText } from 'styling/Typography'
import { Container, ContainerContent, ContainerHeader } from 'components/Containers/Containers'
import { Alert, AlertType } from 'components/Alert/Alert'
import { Button } from 'components/ui/Button/Button'

export const AttendancePolicies: React.FC = () => {
  const { policies } = useSelector((store: RootState) => store.attendance)
  const dispatch = useDispatch<AppDispatch>()
  const [params] = useState<any>(getParams({ location, limit: 1 }))
  const navigate = useNavigate()

  useQueryPagination({
    fetchFunctions: [getPolicies],
    cleanUpFunctions: [clearPolicies],
    params,
  })

  const policy = useMemo(() => policies?.data?.[0] || null, [policies?.data])

  const handleRemove = () => {
    if (!policy) return
    dispatch(
      setConfirmModal({
        isOpen: true,
        onSave: async () => {
          await dispatch(deletePolicy(policy.id))
        },
        setParams: {
          type: ModalTypes.Delete,
        },
        word: 'delete this attendance approval policy',
      }),
    )
  }

  const handleToggleActive = () => {
    if (!policy) return
    dispatch(
      setConfirmModal({
        isOpen: true,
        onSave: async () => {
          await dispatch(activatePolicy(policy.id))
          dispatch(getPolicies(params))
        },
        setParams: {
          type: ModalTypes.BeforeSabe,
        },
        word: policy.isActive
          ? 'deactivate this policy (new attendance reports will be blocked)'
          : 'activate this policy',
      }),
    )
  }

  return (
    <>
      <NavBar />
      <PageWrapper>
        <Flex between center mb='16'>
          <H1WithBack arrow title='Attendance Approval Policy' />
          {policy ? (
            <Button variant='primary' onClick={() => navigate(`edit/${policy.id}`)}>
              Edit Policy
            </Button>
          ) : (
            <Button variant='primary' onClick={() => navigate('edit')}>
              Set Up Policy
            </Button>
          )}
        </Flex>

        <Alert
          title='One policy per company'
          description='This policy controls attendance report approvals. Deactivating it pauses approvals and blocks creation of new reports. Changes apply to new reports only.'
          type={AlertType.INFO}
          colorVariant={ColorVariant.PRIMARY}
        />

        {!policy ? (
          <Flex mt='16'>
            <NoData />
          </Flex>
        ) : (
          <Flex mt='16'>
            <Container>
              <ContainerHeader padding>
                <Flex between center width='100%'>
                  <TitleText weight='700'>{policy.name}</TitleText>
                  <StatusPill status={policy.isActive ? 'success' : 'neutral'}>
                    {policy.isActive ? 'Active' : 'Inactive'}
                  </StatusPill>
                </Flex>
              </ContainerHeader>
              <ContainerContent gap='1rem'>
                {policy.description ? <BodyText>{policy.description}</BodyText> : null}
                <OtherText grey>Created {formatDateForDisplay(policy.createdAt)}</OtherText>
                {!policy.isActive ? (
                  <Alert
                    title='Approvals paused'
                    description='No active policy — new attendance reports cannot be created until this policy is activated.'
                    type={AlertType.WARNING}
                    colorVariant={ColorVariant.PRIMARY}
                  />
                ) : null}
                <Flex gap='0.75rem'>
                  <Button variant='secondary' onClick={handleToggleActive}>
                    {policy.isActive ? 'Deactivate' : 'Activate'}
                  </Button>
                  <Button variant='secondary' onClick={handleRemove}>
                    Delete
                  </Button>
                </Flex>
              </ContainerContent>
            </Container>
          </Flex>
        )}
      </PageWrapper>
    </>
  )
}
